import React from "react";
import { WhatsappLogo, EnvelopeSimple, ArrowSquareOut } from "@phosphor-icons/react";

// Regex ordering matters — most specific first.
// 1. Markdown links: [text](url) — captured whole; we render the label + link.
// 2. Full URLs with scheme: http(s)://…
// 3. Bare domains with common TLDs (.com, .com.do, .net, .org, .do, .app, .io, .co),
//    optionally with path/query. Requires at least one dot and a valid TLD so
//    plain words like "www" alone don't match.
// 4. Emails.
// 5. Dominican phone numbers.
const MD_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const URL_RE = /\bhttps?:\/\/[^\s<>()"']+[^\s<>()"'.,;:!?\]]/gi;
const BARE_DOMAIN_RE = /(?<![@\w.])((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:com\.do|com|net|org|do|app|io|co|es|gob\.do|edu\.do|mil\.do))(\/[^\s<>()"']*)?/gi;
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_RE = /(?<!\d)(?:\+?1[\s\-.]?)?\(?(?:809|829|849)\)?[\s\-.]?\d{3}[\s\-.]?\d{4}/g;

const COMBINED_RE = new RegExp(
  `(${MD_RE.source})|(${URL_RE.source})|(${BARE_DOMAIN_RE.source})|(${EMAIL_RE.source})|(${PHONE_RE.source})`,
  "gi"
);

function normalizeDrPhone(raw) {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return digits;
  return digits;
}

function linkAnchor(key, href, label, testid = "msg-link") {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-primary break-all font-medium"
      data-testid={testid}
    >
      {label}
      <ArrowSquareOut size={10} className="inline shrink-0 opacity-70" />
    </a>
  );
}

/**
 * Linkify plain text: turn URLs, bare domains, markdown [text](url), emails,
 * and Dominican phone numbers into accessible, one-tap actionable anchors.
 * Phone numbers become wa.me links (opens WhatsApp) with a small WhatsApp icon.
 * All links open in a new tab.
 */
export function linkify(text) {
  if (!text || typeof text !== "string") return text;

  const nodes = [];
  let lastIndex = 0;
  let match;
  const re = new RegExp(COMBINED_RE.source, "gi");

  while ((match = re.exec(text)) !== null) {
    const full = match[0];
    const idx = match.index;
    // Group indices: 1=full md, 2=md-label, 3=md-href, 4=url, 5=bare-full, 6=bare-domain, 7=bare-path, 8=email, 9=phone
    const [, mdFull, mdLabel, mdHref, urlToken, bareFull, /*bareDomain*/, /*barePath*/, emailToken, phoneToken] = match;

    if (idx > lastIndex) nodes.push(text.slice(lastIndex, idx));

    if (mdFull) {
      nodes.push(linkAnchor(`md${idx}`, mdHref, mdLabel, "msg-md-link"));
    } else if (urlToken) {
      nodes.push(linkAnchor(`u${idx}`, urlToken, urlToken));
    } else if (bareFull) {
      // Prepend https:// so it opens correctly
      const href = `https://${bareFull}`;
      nodes.push(linkAnchor(`b${idx}`, href, bareFull, "msg-bare-link"));
    } else if (emailToken) {
      nodes.push(
        <a
          key={`e${idx}`}
          href={`mailto:${emailToken}`}
          className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-primary break-all"
          data-testid="msg-email"
        >
          <EnvelopeSimple size={10} className="inline shrink-0" />
          {emailToken}
        </a>
      );
    } else if (phoneToken) {
      const wa = normalizeDrPhone(phoneToken);
      nodes.push(
        <a
          key={`p${idx}`}
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 font-medium border border-emerald-500/30 text-[11px] leading-tight whitespace-nowrap"
          data-testid="msg-whatsapp"
          title={`Abrir chat de WhatsApp con ${phoneToken}`}
        >
          <WhatsappLogo size={11} weight="fill" className="shrink-0" />
          {phoneToken}
        </a>
      );
    }
    lastIndex = idx + full.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.map((n, i) =>
    typeof n === "string" ? <React.Fragment key={`t${i}`}>{n}</React.Fragment> : n
  );
}
